import { Product_Sub_Family_Name_Enum } from '@kheops/graphql';

export type NamingQuestionTranslations = Partial<Record<string, string>>;

export interface NamingQuestion {
  slot: string;
  question: NamingQuestionTranslations;
}

export interface NamingQuestionGroup {
  subFamilies: Product_Sub_Family_Name_Enum[];
  slotOrder: string[];
  questions: Record<string, NamingQuestionTranslations>;
  deterministicSlots?: string[]; // used to handle slots that doesn not require llm suggestions
  numericSlots?: string[]; // slots whose value is a specific number/fact
  optionalSlots?: string[]; // slots the user can mark as not applicable, and that validation must not require
}

export type DeterministicNamingStepFollowUp = 'ageRange';

export interface DeterministicNamingStepOption {
  label: NamingQuestionTranslations;
  appendsLabel: boolean;
  followUp?: DeterministicNamingStepFollowUp;
  followUpQuestion?: NamingQuestionTranslations;
}

export interface DeterministicNamingStep {
  slot: string;
  question: NamingQuestionTranslations;
  options: DeterministicNamingStepOption[];
}

export const IMPORT_CHECK_MAX_PRODUCTS = 25;

export const DETERMINISTIC_NAMING_STEPS: DeterministicNamingStep[] = [
  {
    slot: 'Age',
    question: { fr: 'Ce produit est-il destiné aux enfants ou aux bébés ?' },
    options: [
      { label: { fr: 'Bébé' }, appendsLabel: true, followUp: 'ageRange', followUpQuestion: { fr: "Quelle tranche d'âge ?" } },
      { label: { fr: 'Enfants' }, appendsLabel: true, followUp: 'ageRange', followUpQuestion: { fr: "Quelle tranche d'âge ?" } },
    ],
  },
  {
    slot: 'Target/Age',
    question: { fr: "Ce produit est-il destiné à une tranche d'âge ou un besoin spécifique ?" },
    options: [
      { label: { fr: 'Oui' }, appendsLabel: true, followUp: 'ageRange', followUpQuestion: { fr: "Quelle tranche d'âge ?" } },
    ],
  },
  {
    slot: 'Bone',
    question: { fr: 'Le morceau est-il avec ou sans os ?' },
    options: [
      { label: { fr: 'Avec os' }, appendsLabel: true },
      { label: { fr: 'Sans os' }, appendsLabel: true },
    ],
  },
];

export const NAMING_QUESTION_GROUPS: NamingQuestionGroup[] = [
  {
    subFamilies: [Product_Sub_Family_Name_Enum.NonAlcoholicBeverages],
    slotOrder: ['Product', 'Flavor', 'Age'],
    questions: {
      Product: { fr: 'Quel est le type de boisson ?' },
      Flavor: { fr: 'Quel est le parfum ou la variété de cette boisson ?' },
    },
    deterministicSlots: ['Age'],
    optionalSlots: ['Age'],
  },
  {
    subFamilies: [Product_Sub_Family_Name_Enum.SaltyGrocery, Product_Sub_Family_Name_Enum.SweetGrocery, Product_Sub_Family_Name_Enum.FrozenFood],
    slotOrder: ['Product', 'Flavor', 'Age'],
    questions: {
      Product: { fr: 'Quel est le type de produit ?' },
      Flavor: { fr: 'Quel est le parfum ou la variété de ce produit ?' },
    },
    deterministicSlots: ['Age'],
    optionalSlots: ['Age'],
  },
  {
    subFamilies: [Product_Sub_Family_Name_Enum.Spirits, Product_Sub_Family_Name_Enum.BeerCider],
    slotOrder: ['Product', 'Range'],
    questions: {
      Product: { fr: "Quel est le type d'alcool ?" },
      Range: { fr: 'Quelle est la spécificité ou la couleur de cet alcool ?' },
    },
  },
  {
    subFamilies: [Product_Sub_Family_Name_Enum.WineChampagneSparkling],
    slotOrder: ['Product', 'Color', 'Appellation', 'Vintage'],
    questions: {
      Product: { fr: "Quel est le type de produit ou l'appellation de base ?" },
      Color: { fr: 'Quelle est la couleur ou le style de ce produit ?' },
      Appellation: { fr: 'Quel est le nom du domaine, du château, de la marque ou de la cuvée spécifique ?' },
      Vintage: { fr: 'Si votre produit est millésimé, quelle est son année de récolte ?' },
    },
    numericSlots: ['Vintage'],
    optionalSlots: ['Vintage'],
  },
  {
    subFamilies: [Product_Sub_Family_Name_Enum.Dairy],
    slotOrder: ['Product', 'Milk Treat', 'Milk Type', 'Range', 'Farming', 'Grade', '%Fat'],
    questions: {
      Product: { fr: 'Quel est le nom de base de ce produit ?' },
      'Milk Treat': { fr: 'Quel traitement spécifique a été appliqué au produit ou à sa matière première ?' },
      'Milk Type': { fr: "Quel est le type de lait utilisé ou l'origine principale ?" },
      Range: { fr: 'Quelle est la saveur, le parfum ou la variété spécifique ?' },
      Farming: { fr: "Y a-t-il un mode d'élevage, un label ou une certification spécifique ?" },
      Grade: { fr: 'Quel est le calibre ou la taille de ce produit ?' },
      '%Fat': { fr: 'Quel est le taux de matière grasse de ce produit ?' },
    },
    numericSlots: ['%Fat'],
    optionalSlots: ['Milk Treat', 'Farming', 'Grade', '%Fat'],
  },
  {
    subFamilies: [Product_Sub_Family_Name_Enum.Deli],
    slotOrder: ['Product', 'Preparation', 'Origin/Base', 'Flavor/Variant', 'Label/Certification'],
    questions: {
      Product: { fr: 'Quel est le nom de base de ce produit ?' },
      Preparation: { fr: 'Quel traitement spécifique a été appliqué au produit ou à sa matière première ?' },
      'Origin/Base': { fr: "Quelle est l'origine ou la base principale de ce produit ?" },
      'Flavor/Variant': { fr: 'Quelle est la saveur, le parfum ou la variété spécifique ?' },
      'Label/Certification': { fr: "Y a-t-il un mode d'élevage, un label ou une certification spécifique ?" },
    },
    optionalSlots: ['Preparation', 'Label/Certification'],
  },
  {
    subFamilies: [Product_Sub_Family_Name_Enum.VegetarianVegan],
    slotOrder: ['Product', 'Preparation', 'Base/Ingredient', 'Flavor/Variant', 'Label/Certification'],
    questions: {
      Product: { fr: 'Quel est le nom de base de ce produit ?' },
      Preparation: { fr: 'Quel traitement spécifique a été appliqué au produit ou à sa matière première ?' },
      'Base/Ingredient': { fr: "Quelle est l'origine ou la base principale de ce produit ?" },
      'Flavor/Variant': { fr: 'Quelle est la saveur, le parfum ou la variété spécifique ?' },
      'Label/Certification': { fr: 'Y a-t-il un label ou une certification spécifique associée à ce produit ?' },
    },
    optionalSlots: ['Preparation', 'Label/Certification'],
  },
  {
    subFamilies: [Product_Sub_Family_Name_Enum.Fruits, Product_Sub_Family_Name_Enum.Vegetables],
    slotOrder: ['Product', 'Variety', 'Grade', 'Category', 'Origin'],
    questions: {
      Product: { fr: 'Quel est le type de fruit ou de légume ?' },
      Variety: { fr: 'Quelle est la variété spécifique de ce produit ?' },
      Grade: { fr: 'Quel est le calibre ou la catégorie de qualité de ce produit ?' },
      Category: { fr: 'Précisez la catégorie commerciale (si elle est différente du calibre précédemment saisi)' },
      Origin: { fr: "Quel est le pays ou la région d'origine ?" },
    },
    optionalSlots: ['Category'],
  },
  {
    subFamilies: [Product_Sub_Family_Name_Enum.FishSeafood],
    slotOrder: ['Product', 'Latin name', 'Grade', 'Port', 'Fishing Zone'],
    questions: {
      Product: { fr: 'Quel est le nom commercial de ce produit ?' },
      'Latin name': { fr: "Quel est le nom scientifique (latin) de l'espèce ?" },
      Grade: { fr: 'Quelle est la taille ou le calibre des pièces ?' },
      Port: { fr: 'De quel port le produit a-t-il été débarqué ?' },
      'Fishing Zone': { fr: 'Dans quelle zone le produit a-t-il été pêché ou élevé ?' },
    },
    optionalSlots: ['Latin name', 'Port'],
  },
  {
    subFamilies: [Product_Sub_Family_Name_Enum.Bakery],
    slotOrder: ['Product'],
    questions: {
      Product: { fr: 'Quel est le type de pain ou de pâtisserie ?' },
    },
  },
  {
    subFamilies: [Product_Sub_Family_Name_Enum.HygieneBeauty, Product_Sub_Family_Name_Enum.HouseholdCleaning],
    slotOrder: ['Product', 'Scent', 'Benefit'],
    questions: {
      Product: { fr: 'Quel est le type de produit ?' },
      Scent: { fr: 'Quel est le parfum ou la senteur de ce produit ?' },
      Benefit: { fr: 'Quel est le bénéfice principal ou la propriété de ce produit ?' },
    },
  },
  {
    subFamilies: [Product_Sub_Family_Name_Enum.Pets],
    slotOrder: ['Product', 'Animal/Variety', 'Target/Age', 'Technical trait'],
    questions: {
      Product: { fr: 'Quel est le nom de base de ce produit ?' },
      'Animal/Variety': { fr: 'À quel animal ou variété ce produit correspond-il ?' },
      'Technical trait': { fr: 'Quelle est la caractéristique technique ou la recette majeure ?' },
    },
    deterministicSlots: ['Target/Age'],
    optionalSlots: ['Target/Age', 'Technical trait'],
  },
  {
    subFamilies: [Product_Sub_Family_Name_Enum.GamesToys],
    slotOrder: ['Product', 'Model/Licence', 'Age', 'Technical trait'],
    questions: {
      Product: { fr: 'Quel est le nom de base de ce produit ?' },
      'Model/Licence': { fr: 'Quel est le modèle, la gamme ou la licence ?' },
      'Technical trait': { fr: "Quelle est la caractéristique technique ou l'atout majeur ?" },
    },
    deterministicSlots: ['Age'],
    optionalSlots: ['Age', 'Technical trait'],
  },
  {
    subFamilies: [Product_Sub_Family_Name_Enum.Floral],
    slotOrder: ['Product', 'Variety/Color', 'Technical trait'],
    questions: {
      Product: { fr: 'Quel est le nom de base de ce produit ?' },
      'Variety/Color': { fr: "Quelle est la variété, l'espèce ou la couleur spécifique ?" },
      'Technical trait': { fr: "Quelle est la caractéristique technique ou l'usage principal ?" },
    },
  },
  {
    subFamilies: [Product_Sub_Family_Name_Enum.ClothingTextile],
    slotOrder: ['Product', 'Gender/Cut', 'Color', 'Material', 'Size'],
    questions: {
      Product: { fr: "Quel est le type de vêtement ou d'article textile ?" },
      'Gender/Cut': { fr: 'Pour quel genre ou avec quelle coupe le produit est-il conçu ?' },
      Color: { fr: 'Quelle est la couleur ou le motif de ce produit ?' },
      Material: { fr: 'De quelle matière principale est composé ce produit ?' },
      Size: { fr: "Quelle est la taille de l'article ?" },
    },
  },
  {
    subFamilies: [Product_Sub_Family_Name_Enum.RawMeat],
    slotOrder: ['Species', 'Product', '%Fat', 'Stars', 'Bone', 'Origin'],
    questions: {
      Species: { fr: 'De quelle espèce animale provient la viande ?' },
      Product: { fr: 'Quel est le morceau ou le type de préparation ?' },
      '%Fat': { fr: 'Quel est le pourcentage de matière grasse de ce produit ?' },
      Stars: { fr: "Quel est le niveau de qualité ou le nombre d'étoiles de ce morceau ?" },
      Origin: { fr: "Quelle est l'origine ou la provenance de la viande ?" },
    },
    numericSlots: ['%Fat'],
    deterministicSlots: ['Bone'],
    optionalSlots: ['%Fat', 'Stars', 'Bone'],
  },
];

// Basic, relevant placeholder shown in the custom-input field for each slot, regardless of the family-specific question wording.
const NAMING_QUESTION_PLACEHOLDERS: Record<string, NamingQuestionTranslations> = {
  Product: { fr: 'Nom du produit' },
  Flavor: { fr: 'Parfum du produit' },
  Age: { fr: "Tranche d'âge" },
  'Target/Age': { fr: "Tranche d'âge ou besoin" },
  Range: { fr: 'Gamme ou variante' },
  'Milk Treat': { fr: 'Traitement du lait' },
  'Milk Type': { fr: 'Type de lait' },
  Farming: { fr: "Mode d'élevage ou label" },
  Preparation: { fr: 'Traitement du produit' },
  'Origin/Base': { fr: 'Origine ou base' },
  'Base/Ingredient': { fr: 'Base ou ingrédient principal' },
  'Flavor/Variant': { fr: 'Saveur ou variété' },
  'Label/Certification': { fr: 'Label ou certification' },
  Grade: { fr: 'Calibre du produit' },
  '%Fat': { fr: 'Taux de matière grasse' },
  Variety: { fr: 'Variété du produit' },
  Category: { fr: 'Catégorie commerciale' },
  Origin: { fr: 'Origine du produit' },
  'Latin name': { fr: 'Nom scientifique' },
  Port: { fr: 'Port de débarquement' },
  'Fishing Zone': { fr: 'Zone de pêche' },
  Scent: { fr: 'Senteur du produit' },
  Benefit: { fr: 'Bénéfice principal' },
  'Animal/Variety': { fr: 'Animal ou variété' },
  'Model/Licence': { fr: 'Modèle ou licence' },
  'Variety/Color': { fr: 'Variété ou couleur' },
  'Technical trait': { fr: 'Caractéristique technique' },
  'Gender/Cut': { fr: 'Genre ou coupe' },
  Color: { fr: 'Couleur du produit' },
  Material: { fr: 'Matière principale' },
  Size: { fr: "Taille de l'article" },
  Species: { fr: 'Espèce animale' },
  Stars: { fr: 'Niveau de qualité' },
  Bone: { fr: 'Avec ou sans os' },
  Appellation: { fr: 'Nom du domaine ou de la cuvée' },
  Vintage: { fr: 'Année de récolte' },
};

export function getNamingQuestionPlaceholder(slot: string): NamingQuestionTranslations | undefined {
  return NAMING_QUESTION_PLACEHOLDERS[slot];
}

function findGroup(subFamily: Product_Sub_Family_Name_Enum): NamingQuestionGroup | undefined {
  return NAMING_QUESTION_GROUPS.find((group) => group.subFamilies.includes(subFamily));
}

export function getSlotOrder(subFamily: Product_Sub_Family_Name_Enum): string[] | undefined {
  return findGroup(subFamily)?.slotOrder;
}

export function getNumericSlots(subFamily: Product_Sub_Family_Name_Enum): string[] {
  return findGroup(subFamily)?.numericSlots ?? [];
}

export function getOptionalSlots(subFamily: Product_Sub_Family_Name_Enum): string[] {
  return findGroup(subFamily)?.optionalSlots ?? [];
}

export function getDeterministicNamingStep(slot: string): DeterministicNamingStep | undefined {
  return DETERMINISTIC_NAMING_STEPS.find((step) => step.slot === slot);
}

export function getNamingQuestions(subFamily: Product_Sub_Family_Name_Enum): NamingQuestion[] | undefined {
  const group = findGroup(subFamily);

  if (!group) {
    return undefined;
  }

  const deterministicSlots = new Set(group.deterministicSlots ?? []);

  return group.slotOrder.filter((slot) => !deterministicSlots.has(slot)).map((slot) => ({ slot, question: group.questions[slot] }));
}
